/**
 * Filter builder, driven entirely by the entity's field registry.
 *
 * The registry is the single source of truth: the same list the SQL compiler
 * validates against builds these menus, so the UI cannot offer a filter the
 * server would reject. Operators are narrowed by field kind — offering
 * "contains" on a date is worse than not offering it at all.
 *
 * Applied filters live in the URL, so this component holds state only for the
 * row being composed.
 */

import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { FieldDescriptor, FilterOperator, ViewFilter } from '@erp/contracts'
import type { FieldRegistry } from '@erp/contracts'
import { readFilters, withFilters } from '../view-state'
import { optionsFrom, useIndustries, useLeadSources, useLeadStatuses, useUsers } from '../status-context'
import { Button, Field, Select, TextInput } from '../ui'

const OPERATOR_LABEL: Record<FilterOperator, string> = {
  eq: 'is',
  neq: 'is not',
  in: 'is any of',
  notIn: 'is none of',
  contains: 'contains',
  startsWith: 'starts with',
  gt: 'after',
  gte: 'on or after',
  lt: 'before',
  lte: 'on or before',
  isNull: 'is empty',
  isNotNull: 'is not empty',
  between: 'between',
}

/** Which operators make sense for each kind of field. */
function operatorsFor(kind: FieldDescriptor['kind']): FilterOperator[] {
  switch (kind) {
    case 'text':
      return ['contains', 'startsWith', 'eq', 'neq', 'isNull', 'isNotNull']
    case 'number':
    case 'currency':
      return ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'isNull', 'isNotNull']
    case 'date':
    case 'datetime':
      return ['gte', 'lte', 'between', 'isNull', 'isNotNull']
    case 'boolean':
      return ['eq']
    case 'select':
    case 'user':
    case 'link':
      return ['eq', 'neq', 'in', 'notIn', 'isNull', 'isNotNull']
  }
}

const NO_VALUE: ReadonlySet<FilterOperator> = new Set(['isNull', 'isNotNull'])

export function FilterBuilder({ registry }: { registry: FieldRegistry }) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const applied = readFilters(searchParams)

  const filterable = registry.fields.filter((field) => field.filterable)
  const [field, setField] = useState(filterable[0]?.field ?? '')
  const [operator, setOperator] = useState<FilterOperator>('eq')
  const [value, setValue] = useState('')

  const selected = filterable.find((f) => f.field === field)
  const operators = selected ? operatorsFor(selected.kind) : []
  const needsValue = !NO_VALUE.has(operator)

  function apply(next: ViewFilter[]) {
    navigate({ search: withFilters(searchParams, next).toString() })
  }

  function add() {
    if (!selected) return
    const filter: ViewFilter = NO_VALUE.has(operator)
      ? { field, operator, value: null }
      : { field, operator, value: coerce(selected, operator, value) }
    apply([...applied, filter])
    setValue('')
  }

  return (
    <div className="ax-filters">
      {applied.length > 0 ? (
        <ul className="ax-filter-list">
          {applied.map((filter, index) => (
            <li key={`${filter.field}-${index}`} className="ax-filter-chip">
              <span>{describe(filter, registry)}</span>
              <button
                type="button"
                className="ax-chip-remove"
                aria-label="Remove filter"
                onClick={() => apply(applied.filter((_, i) => i !== index))}
              >
                ×
              </button>
            </li>
          ))}
          <li>
            <button type="button" className="ax-link" onClick={() => apply([])}>
              Clear all
            </button>
          </li>
        </ul>
      ) : null}

      <div className="ax-filter-add">
        <Field label="Field">
          <Select
            name="filterField"
            value={field}
            options={filterable.map((f) => ({ value: f.field, label: f.label }))}
            onChange={(next) => {
              setField(next)
              const kind = filterable.find((f) => f.field === next)?.kind
              // Reset the operator: the previous one may not apply to this kind.
              setOperator(kind ? (operatorsFor(kind)[0] ?? 'eq') : 'eq')
              setValue('')
            }}
          />
        </Field>

        <Field label="Condition">
          <Select
            name="filterOperator"
            value={operator}
            options={operators.map((op) => ({ value: op, label: OPERATOR_LABEL[op] }))}
            onChange={(next) => setOperator(next as FilterOperator)}
          />
        </Field>

        {needsValue && selected ? (
          <Field label="Value">
            <ValueInput descriptor={selected} value={value} onChange={setValue} />
          </Field>
        ) : null}

        <Button onClick={add} disabled={!selected}>
          Add filter
        </Button>
      </div>
    </div>
  )
}

/**
 * Option-backed fields get a picker rather than a free-text box — typing a UUID
 * by hand is not a feature. `optionsSource` on the descriptor says which lookup
 * to draw from.
 */
function ValueInput({
  descriptor,
  value,
  onChange,
}: {
  descriptor: FieldDescriptor
  value: string
  onChange: (value: string) => void
}) {
  const statuses = useLeadStatuses()
  const sources = useLeadSources()
  const industries = useIndustries()
  const users = useUsers()

  if (descriptor.kind === 'user') {
    // Inactive people stay selectable here, unlike in an owner picker: finding
    // the records still owned by someone who left is exactly how you reassign
    // them. Labelled so the difference is visible.
    return (
      <Select
        name="filterValue"
        value={value}
        onChange={onChange}
        options={[
          { value: '', label: 'Select…' },
          ...users.map((user) => ({
            value: user.id,
            label: user.active ? user.name : `${user.name} (inactive)`,
          })),
        ]}
      />
    )
  }

  if (descriptor.optionsSource !== null) {
    const options =
      descriptor.optionsSource === 'lead_status'
        ? statuses
        : descriptor.optionsSource === 'lead_source'
          ? sources
          : descriptor.optionsSource === 'industry'
            ? industries
            : []
    if (options.length > 0) {
      return (
        <Select
          name="filterValue"
          value={value}
          onChange={onChange}
          options={optionsFrom(options, 'Select…')}
        />
      )
    }
  }

  const type =
    descriptor.kind === 'number' || descriptor.kind === 'currency'
      ? 'number'
      : descriptor.kind === 'date'
        ? 'date'
        : descriptor.kind === 'datetime'
          ? 'datetime-local'
          : 'text'

  return (
    <input
      className="ax-input"
      type={type}
      name="filterValue"
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  )
}

/** Query strings are all text; the compiler wants real types. */
function coerce(descriptor: FieldDescriptor, operator: FilterOperator, raw: string): unknown {
  if (operator === 'in' || operator === 'notIn') {
    return raw.split(',').map((part) => part.trim()).filter((part) => part.length > 0)
  }
  if (descriptor.kind === 'number' || descriptor.kind === 'currency') {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : raw
  }
  if (descriptor.kind === 'datetime' && raw.length > 0) {
    return new Date(raw).toISOString()
  }
  return raw
}

function describe(filter: ViewFilter, registry: FieldRegistry): string {
  const label = registry.fields.find((f) => f.field === filter.field)?.label ?? filter.field
  const operator = OPERATOR_LABEL[filter.operator]
  if (NO_VALUE.has(filter.operator)) return `${label} ${operator}`
  const value = Array.isArray(filter.value) ? filter.value.join(', ') : String(filter.value ?? '')
  return `${label} ${operator} ${value}`
}
